<template>
    <main class="layout__main layout__main--no-top-padding">
        <BaseHero
            :image="heroImage"
            :lottie-illustration="lottieIllustration"
            :lottie-loading="lottieLoading"
            :placeholder="pending"
        >
            <template #text>
                <!-- eslint-disable vue/no-v-html -->
                <h2
                    v-if="(home && home.headerText) && !featureFlag('hero-vertical')"
                    data-allow-anchor
                    v-html="home.headerText"
                />
                <!-- eslint-enable vue/no-v-html -->

                <BasePills
                    v-if="home?.selectedSearches && featureFlag('home-selected-searches')"
                    :pills="home?.selectedSearches?.[0]?.searches"
                    :pills-class="`pill--square pill--patients`"
                    :compact="true"
                    :class="`pills--patients`"
                >
                    <template #title>
                        <TheSearchForm
                            :placeholder="home.selectedSearches[0]?.searchPlaceholder"
                            :class="`search-form--largest`"
                            :in-navigation="false"
                        />
                    </template>
                    <template #description>
                        {{ home?.selectedSearches?.[0]?.searches?.length ? home.selectedSearches[0]?.searchDescription : "" }}
                    </template>
                </BasePills>
            </template>

            <template
                v-if="featureFlag('hero-vertical')"
                #after
            >
                <!-- eslint-disable vue/no-v-html -->
                <h2
                    v-if="home && home.headerText"
                    class="hero-vertical-title"
                    data-allow-anchor
                    v-html="home.headerText"
                />
                <!-- eslint-enable vue/no-v-html -->
                <p
                    v-if="(home && home.headerSubtitle)"
                    v-html="home.headerSubtitle"
                    class="lead"
                />
            </template>

            <template #banner>
                <BaseUpcoming
                    v-if="home && home.highlight.length && featureFlag('hero-highlight')"
                    :uri="home.highlight[0].entry.length ? home.highlight[0].entry[0].uri : ''"
                    :class="{
                        'base-upcoming--accent': featureFlag('hero-highlight-accent')
                    }"
                >
                    <template #title>{{ home.highlight[0].title }}</template>
                    <template #default>{{ home.highlight[0].text }}</template>

                    <template
                        v-if="home.highlight[0].entry.length"
                        #meta
                    >
                        <template v-if="featureFlag('hero-highlight-show-date')">
                            {{ formatHighlightDate(home.highlight[0].entry[0]) }}
                        </template>
                        <template v-else>
                            {{ home.highlight[0].entry[0].title }}
                        </template>
                    </template>
                </BaseUpcoming>
            </template>
        </BaseHero>

        <article
            class="bleed"
        >
            <BasePills
                v-if="home && home.selectedTopics?.length && featureFlag('home-selected-topics')"
                :pills="home.selectedTopics[0].topics"
            >
                <template #title>
                    {{ home.selectedTopics[0].title }}
                </template>
                <template #description>
                    {{ home.selectedTopics[0].description }}
                </template>
            </BasePills>

            <BaseSection
                v-if="bot && featureFlag('home-chatbot')"
                class="container-narrow"
            >
                <FlexibleSectionsCardBlock :block="bot" />
            </BaseSection>

            <BaseSection
                v-if="home?.linksList && home.linksList?.length && featureFlag('home-links-list')"
                class="container-narrow"
            >
                <BaseQuicklinkList
                    v-if="home?.linksList && home.linksList?.length && featureFlag('home-links-list')"
                    :links="home.linksList"
                />
            </BaseSection>

            <FlexibleSections
                v-if="featureFlag('home-flex-sections')"
                :sections="home?.sections && Array.isArray(home.sections) ? home.sections : []"
                :placeholder="pending ? 'overview' : undefined"
                data-wide
            />

            <FlexibleSectionsNews
                v-if="featureFlag('home-news')"
                :title="'Updates'"
                :tags="[]"
                :news="[]"
                link="/updates"
                :placeholder="pending ? 'overview' : undefined"
                :hide-meta="true"
                :is-patient-updates="true"
                class="section--home-news"
            >
                <template #title>
                    <h4>Updates</h4>
                </template>
            </FlexibleSectionsNews>
        </article>
    </main>
</template>

<script setup lang="ts">
import HomeQuery from '~/graphql/queries/Home.graphql';

const { activeSite, featureFlag } = useMultisite();
const { variables } = useCraftVariables();
variables.uri = undefined;

const response = await useLazyAsyncQuery({
    query: HomeQuery,
    variables
});

const {
    craftEntry: home,
    pending,
} = usePageQuery(
    response,
    { entryKey: 'home', activeSite }
);

const lottieIllustration = ref('');
const lottieLoading = ref(false);

async function fetchAnimation(url: string) {
    const data = await $fetch<Record<string, unknown>>('/api/animations', {
        query: { url },
    });

    return JSON.stringify(data);
}

watch(() => home?.value?.animatedIllustration?.[0]?.url, async(url) => {
    if (import.meta.dev) {
        // eslint-disable-next-line no-console
        console.info('[lottie:home] CMS asset URL', url || '(empty)');
    }

    lottieIllustration.value = '';

    if (!url) {
        lottieLoading.value = false;
        return;
    }

    lottieLoading.value = true;

    if (import.meta.dev) {
        // eslint-disable-next-line no-console
        console.info('[lottie:home] loading', true);
    }

    try {
        lottieIllustration.value = await fetchAnimation(url);

        if (import.meta.dev) {
            // eslint-disable-next-line no-console
            console.info('[lottie:home] fetched', JSON.stringify({
                bytes: lottieIllustration.value.length,
            }));
        }
    } catch (error) {
        if (import.meta.dev) {
            // eslint-disable-next-line no-console
            console.error('[lottie:home] fetch failed', error);
        }
    } finally {
        lottieLoading.value = false;

        if (import.meta.dev) {
            // eslint-disable-next-line no-console
            console.info('[lottie:home] loading', false);
        }
    }
}, { immediate: true });

function formatHighlightDate(highlight: any) {
    if (!highlight) {
        return;
    }

    // Prefer dateRange
    if (highlight.dateRange) {
        return useDate().formatDateRange(highlight.dateRange.start, highlight.dateRange.end);
    }

    // Fall back to postdate
    return useDate().formatDateRange(highlight.postDate, highlight.postDate);
}

const heroImage = computed(() => {
    if (featureFlag('hero-vertical')) {
        return home?.value?.headerImageFull?.[0] ?? null;
    }

    if (featureFlag('hero-split-layout') && home?.value?.squareImage?.length) {
        return home?.value?.squareImage?.[0] ?? null;
    }

    return home?.value?.headerImage?.[0] ?? null;
});

const bot = computed(() => toValue(home)?.chatBotSelector);
</script>
<style lang="less" scoped>
section:first-child {
    margin-top: 0;
}

.section--home-news {
    margin-top: 5rem;
    margin-bottom: .5rem;
}

.hero-vertical-title {
    .typography-h3();

    @media @q-md-min {
        font-size: 2.125rem;
    }
}

p.lead {
    margin-top: 1.25rem;

    @media @q-md-min {
        margin-top: .5rem;
    }
}
</style>
